import { useEffect, useRef, useState } from 'react';
import { Link, Route, Switch, useLocation } from 'wouter';
import {
  getToken,
  handleAuthCallback,
  setPostAuthPath,
  signInUrl,
  signOut,
  signOutUrl,
} from './auth';
import { ShareModal } from './share-modal';
import { trpc } from './trpc';

/**
 * Phase 1a-ACL: the SPA grows a URL surface (wouter):
 *   /              — signed-in home (create + your galleries); sign-in landing otherwise
 *   /g/:galleryId  — gallery view; role-aware (owner: upload + sharing; viewer:
 *                    photos only; anonymous works for public galleries)
 *   /share/:token  — magic-link redemption; saves the path and bounces through
 *                    Cognito when signed out
 *   /auth/callback — handled imperatively by handleAuthCallback() before render
 *
 * Deep links into private galleries surface sign-in / no-access states from the
 * API's error codes (NOT_FOUND vs FORBIDDEN) rather than the SPA pre-deciding.
 */
export function App() {
  const [bootstrapped, setBootstrapped] = useState(false);
  const [token, setTokenState] = useState<string | null>(null);

  useEffect(() => {
    handleAuthCallback()
      .catch((err) => console.error('callback handler threw', err))
      .finally(() => {
        setTokenState(getToken());
        setBootstrapped(true);
      });
  }, []);

  if (!bootstrapped) {
    return <CenteredMessage>Loading…</CenteredMessage>;
  }

  const signedIn = Boolean(token);
  return (
    <Switch>
      <Route path="/share/:token">
        {(params) => <ShareRedeemScreen token={params.token} signedIn={signedIn} />}
      </Route>
      <Route path="/g/:galleryId">
        {(params) => (
          <Shell signedIn={signedIn} showBack>
            <GalleryScreen galleryId={params.galleryId} signedIn={signedIn} />
          </Shell>
        )}
      </Route>
      <Route path="/account">
        {signedIn ? (
          <Shell signedIn showBack>
            <AccountScreen />
          </Shell>
        ) : (
          <SignInScreen />
        )}
      </Route>
      <Route>
        {signedIn ? (
          <Shell signedIn>
            <HomeScreen />
          </Shell>
        ) : (
          <SignInScreen />
        )}
      </Route>
    </Switch>
  );
}

function Shell({
  signedIn,
  showBack = false,
  children,
}: {
  signedIn: boolean;
  showBack?: boolean;
  children: React.ReactNode;
}) {
  return (
    <main className="min-h-screen bg-neutral-50 text-neutral-900">
      <header className="border-b border-neutral-200 bg-white">
        <div className="max-w-5xl mx-auto px-6 py-4 flex items-center justify-between">
          <h1 className="text-lg font-semibold">
            <Link href="/" className="hover:text-neutral-600">
              gallerywall
            </Link>
          </h1>
          <div className="flex items-center gap-4">
            {signedIn && showBack && (
              <Link href="/" className="text-sm text-neutral-500 hover:text-neutral-900">
                ← galleries
              </Link>
            )}
            {signedIn && (
              <Link href="/account" className="text-sm text-neutral-500 hover:text-neutral-900">
                Account
              </Link>
            )}
            {signedIn ? (
              <button
                type="button"
                onClick={() => {
                  // Revoke the refresh token + clear storage, then end the
                  // Cognito-side session; redirect even if revoke failed.
                  signOut().finally(() => {
                    window.location.href = signOutUrl();
                  });
                }}
                className="text-sm text-neutral-500 hover:text-neutral-900"
              >
                Sign out
              </button>
            ) : (
              <a href={signInUrl()} className="text-sm text-neutral-500 hover:text-neutral-900">
                Sign in
              </a>
            )}
          </div>
        </div>
      </header>

      <div className="max-w-5xl mx-auto px-6 py-10">{children}</div>
    </main>
  );
}

function SignInScreen() {
  return (
    <main className="min-h-screen flex items-center justify-center bg-neutral-50 text-neutral-900">
      <div className="text-center max-w-sm">
        <h1 className="text-3xl font-semibold">gallerywall</h1>
        <p className="text-sm text-neutral-500 mt-2">Sign in to create + manage galleries.</p>
        <a
          href={signInUrl()}
          className="inline-block mt-8 px-5 py-2.5 rounded-md bg-neutral-900 text-white text-sm font-medium hover:bg-neutral-700"
        >
          Sign in
        </a>
      </div>
    </main>
  );
}

function CenteredMessage({ children }: { children: React.ReactNode }) {
  return (
    <main className="min-h-screen flex items-center justify-center bg-neutral-50 text-neutral-500 text-sm">
      {children}
    </main>
  );
}

function HomeScreen() {
  const [, navigate] = useLocation();
  const galleries = trpc.gallery.list.useQuery();

  return (
    <div className="space-y-10">
      <CreateGalleryForm onCreated={(id) => navigate(`/g/${id}`)} />
      {galleries.data && galleries.data.galleries.length > 0 && (
        <div className="max-w-md mx-auto">
          <h2 className="text-sm font-medium text-neutral-700 mb-3">Your galleries</h2>
          <ul className="divide-y divide-neutral-200 bg-white border border-neutral-200 rounded-lg">
            {galleries.data.galleries.map((g) => (
              <li key={g.id}>
                <Link
                  href={`/g/${g.id}`}
                  className="flex items-center justify-between px-4 py-3 hover:bg-neutral-50"
                >
                  <span className="text-sm">{g.name}</span>
                  <VisibilityBadge visibility={g.visibility} />
                </Link>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

function AccountScreen() {
  const [confirmingDelete, setConfirmingDelete] = useState(false);

  // account.export returns a short-lived presigned URL with a
  // Content-Disposition: attachment, so navigating to it downloads the JSON
  // without leaving the page.
  const exportData = trpc.account.export.useMutation({
    onSuccess: (data) => {
      window.location.href = data.url;
    },
  });

  // After deletion the account (and its Cognito user) are gone — end the
  // session and hand off to the Cognito sign-out so nothing dangles.
  const deleteAccount = trpc.account.delete.useMutation({
    onSuccess: () => {
      signOut().finally(() => {
        window.location.href = signOutUrl();
      });
    },
  });
  const deleting = deleteAccount.isPending || deleteAccount.isSuccess;

  return (
    <div className="max-w-md mx-auto space-y-8">
      <div>
        <h2 className="text-lg font-semibold">Account</h2>
        <p className="text-sm text-neutral-500 mt-1">Manage your data.</p>
      </div>

      <section className="bg-white border border-neutral-200 rounded-lg p-6">
        <h3 className="text-sm font-medium text-neutral-800">Download your data</h3>
        <p className="text-sm text-neutral-500 mt-1">
          A JSON export of everything tied to your account — your galleries and photos, who you've
          shared them with, your favorites, and the galleries you can view.
        </p>
        <button
          type="button"
          onClick={() => exportData.mutate()}
          disabled={exportData.isPending}
          className="mt-4 rounded-md bg-neutral-900 text-white text-sm px-4 py-2 disabled:bg-neutral-400"
        >
          {exportData.isPending ? 'Preparing…' : 'Download my data'}
        </button>
        {exportData.isError && (
          <p className="text-sm text-red-600 mt-2">Something went wrong. Please try again.</p>
        )}
      </section>

      <section className="bg-white border border-red-200 rounded-lg p-6">
        <h3 className="text-sm font-medium text-red-700">Delete your account</h3>
        <p className="text-sm text-neutral-500 mt-1">
          Permanently deletes your account and erases all your galleries, photos, and data. This
          can't be undone.
        </p>
        {!confirmingDelete ? (
          <button
            type="button"
            onClick={() => setConfirmingDelete(true)}
            className="mt-4 rounded-md border border-red-300 text-red-700 text-sm px-4 py-2 hover:bg-red-50"
          >
            Delete my account
          </button>
        ) : (
          <div className="mt-4 rounded-md bg-red-50 border border-red-200 p-4">
            <p className="text-sm text-red-800 font-medium">
              Are you absolutely sure? Everything will be permanently erased.
            </p>
            <div className="flex gap-3 mt-3">
              <button
                type="button"
                onClick={() => deleteAccount.mutate()}
                disabled={deleting}
                className="rounded-md bg-red-600 text-white text-sm px-4 py-2 disabled:bg-red-300"
              >
                {deleting ? 'Deleting…' : 'Yes, delete everything'}
              </button>
              <button
                type="button"
                onClick={() => setConfirmingDelete(false)}
                disabled={deleting}
                className="rounded-md border border-neutral-300 text-neutral-700 text-sm px-4 py-2 disabled:opacity-50"
              >
                Cancel
              </button>
            </div>
            {deleteAccount.isError && (
              <p className="text-sm text-red-600 mt-2">
                Couldn't delete the account. Please try again.
              </p>
            )}
          </div>
        )}
      </section>
    </div>
  );
}

function VisibilityBadge({ visibility }: { visibility: string }) {
  const label =
    visibility === 'public' ? 'Public' : visibility === 'link_shareable' ? 'Shareable' : 'Private';
  return (
    <span className="text-xs text-neutral-500 border border-neutral-200 rounded-full px-2 py-0.5">
      {label}
    </span>
  );
}

function CreateGalleryForm({ onCreated }: { onCreated: (id: string) => void }) {
  const [name, setName] = useState('');
  const create = trpc.gallery.create.useMutation({
    onSuccess: (data) => onCreated(data.id),
  });

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        if (name.trim()) create.mutate({ name: name.trim() });
      }}
      className="max-w-md mx-auto bg-white border border-neutral-200 rounded-lg p-6"
    >
      <label htmlFor="name" className="block text-sm font-medium text-neutral-700">
        Gallery name
      </label>
      <input
        id="name"
        type="text"
        value={name}
        onChange={(e) => setName(e.target.value)}
        placeholder="e.g. October trip"
        autoFocus
        className="mt-2 w-full rounded-md border border-neutral-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-neutral-400"
      />
      <button
        type="submit"
        disabled={!name.trim() || create.isPending}
        className="mt-4 w-full rounded-md bg-neutral-900 text-white py-2 disabled:bg-neutral-400"
      >
        {create.isPending ? 'Creating…' : 'Create gallery'}
      </button>
      {create.error && <p className="mt-3 text-sm text-red-700">{create.error.message}</p>}
    </form>
  );
}

function ShareRedeemScreen({ token, signedIn }: { token: string; signedIn: boolean }) {
  const [, navigate] = useLocation();
  const redeem = trpc.share.redeem.useMutation({
    onSuccess: (data) => navigate(`/g/${data.galleryId}`, { replace: true }),
  });
  // StrictMode double-fires effects in dev; the ref makes redemption (which
  // consumes a use) fire exactly once per mount.
  const fired = useRef(false);

  useEffect(() => {
    if (!signedIn) {
      // Round-trip through Cognito and come back to this exact URL.
      setPostAuthPath(window.location.pathname);
      window.location.href = signInUrl();
      return;
    }
    if (!fired.current) {
      fired.current = true;
      redeem.mutate({ token });
    }
  }, [signedIn, token]);

  if (!signedIn) return <CenteredMessage>Redirecting to sign-in…</CenteredMessage>;
  if (redeem.error) {
    return (
      <main className="min-h-screen flex items-center justify-center bg-neutral-50 text-neutral-900">
        <div className="text-center max-w-sm">
          <h1 className="text-xl font-semibold">This link didn’t work</h1>
          <p className="text-sm text-neutral-500 mt-2">{redeem.error.message}</p>
          <Link
            href="/"
            className="inline-block mt-6 text-sm text-neutral-700 underline hover:text-neutral-900"
          >
            Go to your galleries
          </Link>
        </div>
      </main>
    );
  }
  return <CenteredMessage>Opening shared gallery…</CenteredMessage>;
}

const ALLOWED_MIME = /^image\/(jpeg|png|webp|avif|heic|heif)$/;

function GalleryScreen({ galleryId, signedIn }: { galleryId: string; signedIn: boolean }) {
  const utils = trpc.useUtils();
  // photoId → 0-100 while a PUT is in flight; tiles render a progress bar
  // until the pipeline flips the row past 'uploading'.
  const [uploadProgress, setUploadProgress] = useState<Record<string, number>>({});
  // Non-null while the share modal is open (holds the URL being shared).
  const [shareUrl, setShareUrl] = useState<string | null>(null);
  // photoId currently being prepared for download (button shows a spinner).
  const [downloadingId, setDownloadingId] = useState<string | null>(null);
  const download = trpc.gallery.requestDownload.useMutation();
  // Owner-only view filter: show just the favorited photos (the proofing view).
  const [favoritesOnly, setFavoritesOnly] = useState(false);
  const setFavorite = trpc.favorite.set.useMutation({
    onSuccess: () => utils.gallery.get.invalidate({ galleryId }),
  });

  async function handleDownload(photoId: string) {
    setDownloadingId(photoId);
    try {
      const { url } = await download.mutateAsync({ galleryId, photoId });
      // The signed URL's Content-Disposition: attachment header drives the
      // save; clicking an anchor starts the download without navigating away.
      const a = document.createElement('a');
      a.href = url;
      a.rel = 'noreferrer';
      document.body.appendChild(a);
      a.click();
      a.remove();
    } finally {
      setDownloadingId(null);
    }
  }
  const query = trpc.gallery.get.useQuery(
    { galleryId },
    {
      // NOT_FOUND / FORBIDDEN are stable answers — retrying won't change them.
      retry: false,
      // While anything is mid-pipeline (uploading / processing), refetch
      // every 2s so the UI flips to ready without manual reload. Once all
      // rows are settled, stop polling — the data is now immutable per
      // variant URL's content-addressed key.
      refetchInterval: (q) => {
        const data = q.state.data;
        if (!data) return false;
        const settling = data.photos.some(
          (p) => p.status === 'uploading' || p.status === 'processing',
        );
        return settling ? 2000 : false;
      },
    },
  );

  if (query.error) {
    const code = query.error.data?.code;
    return (
      <div className="text-center py-16 max-w-sm mx-auto">
        <h2 className="text-xl font-semibold">
          {code === 'FORBIDDEN' ? 'You don’t have access to this gallery' : 'Gallery not found'}
        </h2>
        {signedIn && code === 'FORBIDDEN' ? (
          <RequestAccessPrompt galleryId={galleryId} onGranted={() => query.refetch()} />
        ) : (
          <p className="text-sm text-neutral-500 mt-2">
            {signedIn
              ? 'The link may be wrong, or the gallery may be private.'
              : 'If this gallery was shared with you, sign in to view it.'}
          </p>
        )}
        {!signedIn && (
          <a
            href={signInUrl()}
            onClick={() => setPostAuthPath(window.location.pathname)}
            className="inline-block mt-6 px-5 py-2.5 rounded-md bg-neutral-900 text-white text-sm font-medium hover:bg-neutral-700"
          >
            Sign in
          </a>
        )}
      </div>
    );
  }

  const role = query.data?.role ?? null;
  const galleryName = query.data?.gallery.name ?? '';
  // A public gallery's own URL is shareable by ANYONE who can see it (owner,
  // viewer, even anonymous) — that's the "share from the viewer role" piece.
  // Non-public galleries are shared via owner-minted invite links (the panel).
  const canShareGalleryUrl = query.data?.gallery.visibility === 'public';
  return (
    <div className="space-y-8">
      <div className="flex items-baseline justify-between gap-4">
        <h2 className="text-2xl font-semibold">{query.data?.gallery.name ?? 'Loading…'}</h2>
        <div className="flex items-center gap-4 shrink-0">
          {role === 'owner' && (query.data?.photos.some((p) => p.favoriteCount > 0) ?? false) && (
            <button
              type="button"
              onClick={() => setFavoritesOnly((v) => !v)}
              aria-pressed={favoritesOnly}
              className={`rounded-md border px-3 py-1.5 text-sm ${
                favoritesOnly
                  ? 'border-neutral-900 bg-neutral-900 text-white'
                  : 'border-neutral-300 text-neutral-700 hover:border-neutral-500'
              }`}
            >
              ♥ Favorites
            </button>
          )}
          {signedIn && (query.data?.photos.some((p) => p.status === 'ready') ?? false) && (
            <BulkDownloadButton galleryId={galleryId} />
          )}
          {canShareGalleryUrl && (
            <button
              type="button"
              onClick={() => setShareUrl(`${window.location.origin}/g/${galleryId}`)}
              className="rounded-md border border-neutral-300 px-3 py-1.5 text-sm text-neutral-700 hover:border-neutral-500"
            >
              Share
            </button>
          )}
          <p className="text-sm text-neutral-500">
            {query.data?.photos.length ?? 0} photo
            {query.data?.photos.length === 1 ? '' : 's'}
          </p>
        </div>
      </div>

      {shareUrl && (
        <ShareModal
          url={shareUrl}
          title={galleryName || 'gallery'}
          message={galleryName || 'this gallery'}
          onClose={() => setShareUrl(null)}
        />
      )}

      {role === 'owner' && (
        <UploadZone
          galleryId={galleryId}
          onRowsCreated={() => utils.gallery.get.invalidate({ galleryId })}
          onProgress={(photoId, pct) =>
            setUploadProgress((prev) =>
              prev[photoId] === pct ? prev : { ...prev, [photoId]: pct },
            )
          }
        />
      )}

      {query.data && query.data.photos.length > 0 && (
        <div className="columns-2 sm:columns-3 lg:columns-4 gap-4">
          {[...query.data.photos]
            .sort((a, b) => a.position - b.position)
            .filter((p) => !favoritesOnly || p.favoriteCount > 0)
            .map((p) => (
              <PhotoTile
                key={p.id}
                photo={p}
                uploadPct={uploadProgress[p.id]}
                // Per-photo download requires an account (the API gates it),
                // so only offer it to signed-in viewers.
                onDownload={signedIn ? () => handleDownload(p.id) : undefined}
                isDownloading={downloadingId === p.id}
                // Favoriting also requires an account; owners additionally see
                // who favorited (favoritedBy is owner-only from the API).
                onToggleFavorite={
                  signedIn
                    ? () =>
                        setFavorite.mutate({
                          galleryId,
                          photoId: p.id,
                          favorited: !p.favoritedByMe,
                        })
                    : undefined
                }
              />
            ))}
        </div>
      )}

      {role === 'owner' && query.data && (
        <SharePanel
          galleryId={galleryId}
          visibility={query.data.gallery.visibility}
          hasWatermark={query.data.gallery.hasWatermark}
        />
      )}
    </div>
  );
}

/** PUT a file to a pre-signed URL via XHR — fetch can't report upload progress. */
function putWithProgress(
  url: string,
  file: File,
  onProgress: (pct: number) => void,
): Promise<void> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('PUT', url);
    xhr.setRequestHeader('Content-Type', file.type);
    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable) onProgress(Math.round((e.loaded / e.total) * 100));
    };
    xhr.onload = () =>
      xhr.status >= 200 && xhr.status < 300
        ? resolve()
        : reject(new Error(`Upload failed for ${file.name}: ${xhr.status}`));
    xhr.onerror = () => reject(new Error(`Upload failed for ${file.name}: network error`));
    xhr.send(file);
  });
}

function UploadZone({
  galleryId,
  onRowsCreated,
  onProgress,
}: {
  galleryId: string;
  onRowsCreated: () => void;
  onProgress: (photoId: string, pct: number) => void;
}) {
  const requestUpload = trpc.gallery.requestUpload.useMutation();
  const [error, setError] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const [dragging, setDragging] = useState(false);
  // dragenter/dragleave fire for every child element crossed; depth counting
  // is the standard fix so the highlight doesn't flicker.
  const dragDepth = useRef(0);

  async function handleFiles(fileList: FileList | null) {
    if (!fileList || fileList.length === 0) return;
    setError(null);

    const accepted = Array.from(fileList).filter((f) => ALLOWED_MIME.test(f.type));
    const rejected = fileList.length - accepted.length;
    if (rejected > 0) {
      setError(`${rejected} file(s) skipped (unsupported type)`);
    }
    if (accepted.length === 0) return;

    setUploading(true);
    try {
      const result = await requestUpload.mutateAsync({
        galleryId,
        files: accepted.map((f) => ({ contentType: f.type, sizeBytes: f.size })),
      });

      // Surface the rows at status='uploading' BEFORE the PUTs so the masonry
      // shows a tile-with-progress-bar per file while bytes move.
      onRowsCreated();

      // PUT all files in parallel directly to S3 via the pre-signed URLs.
      // Bytes never touch our Lambda. The arrays line up by index (we
      // constructed `result.uploads` from `accepted` 1:1 just above), so pair
      // them explicitly to keep TS happy.
      const pairs = result.uploads.map((upload, i) => ({
        upload,
        file: accepted[i] as File,
      }));
      await Promise.all(
        pairs.map(({ upload, file }) =>
          putWithProgress(upload.uploadUrl, file, (pct) => onProgress(upload.photoId, pct)),
        ),
      );
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Upload failed');
    } finally {
      setUploading(false);
      // Whether PUTs succeeded or failed, sync the rows' latest status; the
      // gallery query's refetchInterval takes over from here.
      onRowsCreated();
    }
  }

  return (
    <>
      <label
        htmlFor="file-input"
        onDragEnter={(e) => {
          e.preventDefault();
          dragDepth.current += 1;
          setDragging(true);
        }}
        onDragOver={(e) => e.preventDefault()}
        onDragLeave={() => {
          dragDepth.current -= 1;
          if (dragDepth.current <= 0) {
            dragDepth.current = 0;
            setDragging(false);
          }
        }}
        onDrop={(e) => {
          e.preventDefault();
          dragDepth.current = 0;
          setDragging(false);
          if (!uploading) handleFiles(e.dataTransfer.files);
        }}
        className={`block border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-colors focus-within:ring-2 focus-within:ring-neutral-500 ${
          dragging
            ? 'border-neutral-700 bg-neutral-100'
            : 'border-neutral-300 hover:border-neutral-500'
        } ${uploading ? 'opacity-50 pointer-events-none' : ''}`}
      >
        <input
          id="file-input"
          type="file"
          multiple
          accept="image/jpeg,image/png,image/webp,image/avif,image/heic,image/heif"
          aria-label="Add photos"
          className="sr-only"
          onChange={(e) => handleFiles(e.target.files)}
          disabled={uploading}
        />
        <div className="text-neutral-700 font-medium">
          {uploading ? 'Uploading…' : dragging ? 'Drop to upload' : 'Click or drag photos here'}
        </div>
        <div className="text-xs text-neutral-500 mt-1">
          JPEG / PNG / WebP / AVIF / HEIC, up to 100 MB each
        </div>
      </label>

      {error && (
        <div
          role="alert"
          className="rounded-md bg-red-50 border border-red-200 text-red-800 px-4 py-3 text-sm"
        >
          {error}
        </div>
      )}
    </>
  );
}

function RequestAccessPrompt({
  galleryId,
  onGranted,
}: {
  galleryId: string;
  onGranted: () => void;
}) {
  const request = trpc.access.request.useMutation({
    onSuccess: (data) => {
      // already_member means access landed between page load and the click
      // (e.g. the owner just approved an earlier request) — re-pull the gallery.
      if (data.status === 'already_member') onGranted();
    },
  });

  if (request.data?.status === 'pending') {
    return (
      <p className="text-sm text-neutral-500 mt-2">
        Request sent — the photographer will see it next time they open the gallery.
      </p>
    );
  }
  return (
    <div className="mt-2">
      <p className="text-sm text-neutral-500">Ask the photographer to share it with you.</p>
      <button
        type="button"
        onClick={() => request.mutate({ galleryId })}
        disabled={request.isPending}
        className="inline-block mt-6 px-5 py-2.5 rounded-md bg-neutral-900 text-white text-sm font-medium hover:bg-neutral-700 disabled:bg-neutral-400"
      >
        {request.isPending ? 'Sending…' : 'Request access'}
      </button>
      {request.error && <p className="mt-3 text-sm text-red-700">{request.error.message}</p>}
    </div>
  );
}

const VISIBILITY_OPTIONS = [
  { value: 'private', label: 'Private', help: 'Only people you add can view' },
  { value: 'link_shareable', label: 'Shareable', help: 'People you add or invite by link' },
  { value: 'public', label: 'Public', help: 'Anyone with the URL can view' },
] as const;

function SharePanel({
  galleryId,
  visibility,
  hasWatermark,
}: {
  galleryId: string;
  visibility: 'private' | 'link_shareable' | 'public';
  hasWatermark: boolean;
}) {
  const utils = trpc.useUtils();
  const setVisibility = trpc.gallery.setVisibility.useMutation({
    onSuccess: () => utils.gallery.get.invalidate({ galleryId }),
  });

  return (
    <section className="bg-white border border-neutral-200 rounded-lg p-6 space-y-8">
      <div>
        <h3 className="text-sm font-semibold text-neutral-900">Sharing</h3>
        <div className="mt-3 flex gap-2" role="radiogroup" aria-label="Gallery visibility">
          {VISIBILITY_OPTIONS.map((opt) => (
            <button
              key={opt.value}
              type="button"
              role="radio"
              aria-checked={visibility === opt.value}
              title={opt.help}
              disabled={setVisibility.isPending}
              onClick={() => setVisibility.mutate({ galleryId, visibility: opt.value })}
              className={`px-3 py-1.5 rounded-md text-sm border ${
                visibility === opt.value
                  ? 'border-neutral-900 bg-neutral-900 text-white'
                  : 'border-neutral-300 text-neutral-700 hover:border-neutral-500'
              }`}
            >
              {opt.label}
            </button>
          ))}
        </div>
        <p className="text-xs text-neutral-500 mt-2">
          {VISIBILITY_OPTIONS.find((o) => o.value === visibility)?.help}
        </p>
      </div>

      <PendingRequestsSection galleryId={galleryId} />
      <AclSection galleryId={galleryId} />
      <ShareLinkSection galleryId={galleryId} />
      <WatermarkSection galleryId={galleryId} hasWatermark={hasWatermark} />
    </section>
  );
}

function WatermarkSection({
  galleryId,
  hasWatermark,
}: {
  galleryId: string;
  hasWatermark: boolean;
}) {
  const utils = trpc.useUtils();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const requestUpload = trpc.gallery.requestWatermarkUpload.useMutation();
  const setWatermark = trpc.gallery.setWatermark.useMutation();
  const clearWatermark = trpc.gallery.clearWatermark.useMutation({
    onSuccess: () => utils.gallery.get.invalidate({ galleryId }),
  });

  async function handleFile(file: File | undefined) {
    if (!file) return;
    setError(null);
    if (!/^image\/(png|webp)$/.test(file.type)) {
      setError('Watermark must be a PNG or WebP image.');
      return;
    }
    setBusy(true);
    try {
      const { uploadUrl, key } = await requestUpload.mutateAsync({
        galleryId,
        contentType: file.type,
      });
      const put = await fetch(uploadUrl, {
        method: 'PUT',
        headers: { 'Content-Type': file.type },
        body: file,
      });
      if (!put.ok) throw new Error(`Upload failed: ${put.status}`);
      await setWatermark.mutateAsync({ galleryId, key });
      await utils.gallery.get.invalidate({ galleryId });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Watermark upload failed');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div>
      <h4 className="text-sm font-medium text-neutral-700">Watermark</h4>
      <p className="text-xs text-neutral-500 mt-1">
        A PNG/WebP overlay burned into the displayed images (not the downloadable originals).
        Applies to photos uploaded after you set it.
      </p>
      <div className="mt-2 flex items-center gap-3">
        <label
          className={`rounded-md border border-neutral-300 px-3 py-1.5 text-sm text-neutral-700 hover:border-neutral-500 ${
            busy ? 'opacity-50 pointer-events-none' : 'cursor-pointer'
          }`}
        >
          <input
            type="file"
            accept="image/png,image/webp"
            className="sr-only"
            disabled={busy}
            onChange={(e) => handleFile(e.target.files?.[0])}
          />
          {busy ? 'Uploading…' : hasWatermark ? 'Replace watermark' : 'Upload watermark'}
        </label>
        {hasWatermark && (
          <>
            <span className="text-xs text-neutral-500">Watermark set</span>
            <button
              type="button"
              onClick={() => clearWatermark.mutate({ galleryId })}
              disabled={clearWatermark.isPending}
              className="text-xs text-neutral-600 hover:text-red-700"
            >
              Remove
            </button>
          </>
        )}
      </div>
      {error && <p className="mt-2 text-xs text-red-700">{error}</p>}
    </div>
  );
}

function PendingRequestsSection({ galleryId }: { galleryId: string }) {
  const utils = trpc.useUtils();
  const pending = trpc.access.listPending.useQuery({ galleryId });
  const resolve = trpc.access.resolve.useMutation({
    onSuccess: () => {
      utils.access.listPending.invalidate({ galleryId });
      utils.acl.list.invalidate({ galleryId });
    },
  });

  if (!pending.data || pending.data.requests.length === 0) return null;
  return (
    <div>
      <h4 className="text-sm font-medium text-neutral-700">Access requests</h4>
      <ul className="mt-2 divide-y divide-neutral-100">
        {pending.data.requests.map((req) => (
          <li key={req.id} className="flex items-center justify-between py-2 text-sm">
            <span>
              {req.email}
              {req.message && (
                <span className="ml-2 text-xs text-neutral-600">“{req.message}”</span>
              )}
            </span>
            <span className="flex gap-3 shrink-0">
              <button
                type="button"
                onClick={() => resolve.mutate({ requestId: req.id, action: 'approve' })}
                disabled={resolve.isPending}
                className="text-xs text-neutral-700 underline hover:text-neutral-900"
              >
                Approve
              </button>
              <button
                type="button"
                onClick={() => resolve.mutate({ requestId: req.id, action: 'deny' })}
                disabled={resolve.isPending}
                className="text-xs text-neutral-600 hover:text-red-700"
              >
                Deny
              </button>
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function AclSection({ galleryId }: { galleryId: string }) {
  const utils = trpc.useUtils();
  const list = trpc.acl.list.useQuery({ galleryId });
  const [email, setEmail] = useState('');
  const add = trpc.acl.add.useMutation({
    onSuccess: () => {
      setEmail('');
      utils.acl.list.invalidate({ galleryId });
    },
  });
  const remove = trpc.acl.remove.useMutation({
    onSuccess: () => utils.acl.list.invalidate({ galleryId }),
  });

  return (
    <div>
      <h4 className="text-sm font-medium text-neutral-700">People with access</h4>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          if (email.trim()) add.mutate({ galleryId, email: email.trim() });
        }}
        className="mt-2 flex gap-2"
      >
        <input
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="name@example.com"
          aria-label="Email to invite"
          className="flex-1 rounded-md border border-neutral-300 px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-neutral-400"
        />
        <button
          type="submit"
          disabled={!email.trim() || add.isPending}
          className="rounded-md bg-neutral-900 text-white px-4 py-1.5 text-sm disabled:bg-neutral-400"
        >
          Add
        </button>
      </form>
      {add.error && <p className="mt-2 text-xs text-red-700">{add.error.message}</p>}

      {list.data && list.data.entries.length > 0 && (
        <ul className="mt-3 divide-y divide-neutral-100">
          {list.data.entries.map((entry) => (
            <li key={entry.id} className="flex items-center justify-between py-2 text-sm">
              <span>
                {entry.email}
                <span className="ml-2 text-xs text-neutral-600">
                  {entry.status === 'accepted' ? 'has viewed' : 'invited'}
                </span>
              </span>
              <button
                type="button"
                onClick={() => remove.mutate({ galleryId, email: entry.email })}
                disabled={remove.isPending}
                className="text-xs text-neutral-600 hover:text-red-700"
              >
                Remove
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function ShareLinkSection({ galleryId }: { galleryId: string }) {
  const utils = trpc.useUtils();
  const list = trpc.share.list.useQuery({ galleryId });
  // The full URL is only ever returned by `create` — surface it once via the
  // share modal so the owner can copy OR send it; reloading loses it by design.
  const [createdUrl, setCreatedUrl] = useState<string | null>(null);
  const create = trpc.share.create.useMutation({
    onSuccess: (data) => {
      setCreatedUrl(data.url);
      utils.share.list.invalidate({ galleryId });
    },
  });
  const revoke = trpc.share.revoke.useMutation({
    onSuccess: () => utils.share.list.invalidate({ galleryId }),
  });

  const activeLinks = (list.data?.links ?? []).filter((l) => !l.revokedAt);

  return (
    <div>
      <div className="flex items-center justify-between">
        <h4 className="text-sm font-medium text-neutral-700">Invite links</h4>
        <button
          type="button"
          onClick={() => create.mutate({ galleryId })}
          disabled={create.isPending}
          className="rounded-md border border-neutral-300 px-3 py-1.5 text-sm text-neutral-700 hover:border-neutral-500 disabled:opacity-50"
        >
          {create.isPending ? 'Creating…' : 'New invite link'}
        </button>
      </div>
      <p className="text-xs text-neutral-500 mt-1">
        Anyone who opens an invite link and signs in gets added to the access list. Links expire
        after 30 days.
      </p>
      {create.error && <p className="mt-2 text-xs text-red-700">{create.error.message}</p>}

      {createdUrl && (
        <ShareModal
          url={createdUrl}
          title="Gallery invite"
          message="invite link"
          onClose={() => setCreatedUrl(null)}
        />
      )}

      {activeLinks.length > 0 && (
        <ul className="mt-3 divide-y divide-neutral-100">
          {activeLinks.map((link) => (
            <li key={link.id} className="flex items-center justify-between py-2 text-sm">
              <span className="text-neutral-700">
                {link.label ?? 'Invite link'}
                <span className="ml-2 text-xs text-neutral-600">
                  {link.useCount} use{link.useCount === 1 ? '' : 's'} · expires{' '}
                  {new Date(link.expiresAt).toLocaleDateString()}
                </span>
              </span>
              <button
                type="button"
                onClick={() => revoke.mutate({ linkId: link.id })}
                disabled={revoke.isPending}
                className="text-xs text-neutral-600 hover:text-red-700"
              >
                Revoke
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function BulkDownloadButton({ galleryId }: { galleryId: string }) {
  const [jobId, setJobId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  // Guards the one-shot auto-download once the zip goes ready (the status
  // query keeps returning 'ready', but we only want to trigger the save once).
  const delivered = useRef(false);

  const request = trpc.gallery.requestBulkDownload.useMutation({
    onSuccess: (data) => {
      delivered.current = false;
      setError(null);
      setJobId(data.jobId);
    },
    onError: (e) => setError(e.message),
  });

  const status = trpc.gallery.bulkDownloadStatus.useQuery(
    { jobId: jobId ?? '' },
    {
      enabled: jobId !== null,
      retry: false,
      refetchInterval: (q) => (q.state.data?.status === 'pending' ? 3000 : false),
    },
  );

  const state = status.data?.status;

  useEffect(() => {
    if (state === 'ready' && status.data?.downloadUrl && !delivered.current) {
      delivered.current = true;
      const a = document.createElement('a');
      a.href = status.data.downloadUrl;
      document.body.appendChild(a);
      a.click();
      a.remove();
    }
    if (state === 'failed') setError(status.data?.error ?? 'Download preparation failed');
  }, [state, status.data]);

  const pending = request.isPending || (jobId !== null && state !== 'ready' && state !== 'failed');
  const label = state === 'ready' ? 'Download ready' : pending ? 'Preparing…' : 'Download all';

  return (
    <div className="flex flex-col items-end">
      <button
        type="button"
        disabled={pending}
        onClick={() => {
          if (state === 'ready' && status.data?.downloadUrl) {
            // Re-download the already-built zip without re-zipping.
            const a = document.createElement('a');
            a.href = status.data.downloadUrl;
            document.body.appendChild(a);
            a.click();
            a.remove();
            return;
          }
          request.mutate({ galleryId });
        }}
        className="rounded-md border border-neutral-300 px-3 py-1.5 text-sm text-neutral-700 hover:border-neutral-500 disabled:opacity-60"
      >
        {label}
      </button>
      {state === 'ready' && (
        <span className="mt-1 text-xs text-neutral-500">Also emailed to you</span>
      )}
      {error && <span className="mt-1 text-xs text-red-700">{error}</span>}
    </div>
  );
}

type Photo = {
  id: string;
  status: string;
  thumbUrl: string | null;
  webUrl: string | null;
  favoriteCount: number;
  favoritedByMe: boolean;
  /** Owner-only: emails of who favorited. Undefined for non-owners. */
  favoritedBy?: string[];
};

function PhotoTile({
  photo,
  uploadPct,
  onDownload,
  isDownloading,
  onToggleFavorite,
}: {
  photo: Photo;
  uploadPct?: number;
  onDownload?: () => void;
  isDownloading?: boolean;
  onToggleFavorite?: () => void;
}) {
  return (
    <div className="group relative mb-4 break-inside-avoid rounded-md overflow-hidden bg-white border border-neutral-200">
      {photo.status === 'ready' && photo.thumbUrl ? (
        <>
          <img src={photo.thumbUrl} alt="" loading="lazy" className="w-full h-auto block" />
          {onDownload && (
            <button
              type="button"
              onClick={onDownload}
              disabled={isDownloading}
              aria-label="Download original"
              className="absolute top-2 right-2 rounded-md bg-black/55 px-2 py-1 text-xs font-medium text-white opacity-0 transition-opacity group-hover:opacity-100 focus:opacity-100 disabled:opacity-100"
            >
              {isDownloading ? 'Preparing…' : 'Download'}
            </button>
          )}
          {/* Heart toggle (signed-in viewers) + count. The count badge shows
              for anyone when >0; the toggle only when onToggleFavorite is set. */}
          {(onToggleFavorite || photo.favoriteCount > 0) && (
            <button
              type="button"
              onClick={onToggleFavorite}
              disabled={!onToggleFavorite}
              aria-pressed={photo.favoritedByMe}
              aria-label={photo.favoritedByMe ? 'Remove favorite' : 'Add favorite'}
              className={`absolute bottom-2 left-2 flex items-center gap-1 rounded-full bg-black/55 px-2 py-1 text-xs font-medium text-white ${
                onToggleFavorite ? 'cursor-pointer' : 'cursor-default'
              } ${photo.favoritedByMe || photo.favoriteCount > 0 ? 'opacity-100' : 'opacity-0 group-hover:opacity-100 focus:opacity-100'}`}
            >
              <span className={photo.favoritedByMe ? 'text-red-400' : ''}>
                {photo.favoritedByMe ? '♥' : '♡'}
              </span>
              {photo.favoriteCount > 0 && <span>{photo.favoriteCount}</span>}
            </button>
          )}
          {/* Owner-only attribution: who favorited this photo. */}
          {photo.favoritedBy && photo.favoritedBy.length > 0 && (
            <div
              className="px-2 py-1 text-[11px] text-neutral-500 truncate"
              title={photo.favoritedBy.join(', ')}
            >
              ♥ {photo.favoritedBy.join(', ')}
            </div>
          )}
        </>
      ) : (
        <div className="aspect-square flex flex-col items-center justify-center gap-2 text-xs text-neutral-500 bg-neutral-100 px-4">
          {photo.status === 'uploading' && uploadPct !== undefined ? (
            <>
              <div
                role="progressbar"
                aria-valuenow={uploadPct}
                aria-valuemin={0}
                aria-valuemax={100}
                aria-label="Upload progress"
                className="w-full max-w-[120px] h-1.5 rounded-full bg-neutral-200 overflow-hidden"
              >
                <div
                  className="h-full bg-neutral-700 transition-[width] duration-200"
                  style={{ width: `${uploadPct}%` }}
                />
              </div>
              <span>{uploadPct < 100 ? `${uploadPct}%` : 'Finishing…'}</span>
            </>
          ) : (
            <StatusBadge status={photo.status} />
          )}
        </div>
      )}
    </div>
  );
}

function StatusBadge({ status }: { status: string }) {
  const label =
    status === 'uploading'
      ? 'Uploading…'
      : status === 'processing'
        ? 'Processing…'
        : status === 'failed'
          ? 'Failed'
          : status;
  const color = status === 'failed' ? 'text-red-700' : 'text-neutral-500';
  return <span className={color}>{label}</span>;
}
